import { t } from '@lingui/core/macro';
import {
    type Quantity,
    type QuantityType,
    type QuantityUnit,
    type SourcingAvailability,
    SourcingAvailabilityStatus,
} from '@luminovo/api-contracts';
import { isPresent, MonetaryValue } from '@luminovo/commons';
// Built on TanStack's own helper, so it names TanStack's column types.
import {
    AccessorFn,
    CellContext,
    ColumnDefTemplate,
    ColumnMeta,
    DeepKeys,
    DeepValue,
    DisplayColumnDef,
    GroupColumnDef,
    IdentifiedColumnDef,
    Row,
    RowData,
    ColumnHelper as TanStackColumnHelper,
    createColumnHelper as tanStackCreateColumnHelper,
} from '@tanstack/react-table';

import { formatAvailabilityDuration } from '../formatters/formatAvailabilityDuration';
import { formatQuantity, formatQuantityUnit } from '../formatters/formatQuantity';
import { ColumnDef, DataStackFeatures } from './tanstackTypes';
import {
    AdditionalFilter,
    ArraryFilterConfig,
    AvailabilityRenderOption,
    ColumnSource,
    DateFilterConfig,
    DateRange,
    EmptyCell,
    EnumFilterConfig,
    MonetaryValueFilterConfig,
    MonetaryValueRenderOption,
    NumberFilterConfig,
    NumberRange,
    QuantityFilterConfig,
    QuantityRenderOption,
    QuickFilter,
    RenderOptionConfig,
    SupportedFilterFn,
    TextFilterConfig,
} from './type';
import { createPositionBasedSortFunction } from './utils';

type RequiredColumnDef<TData extends RowData, TValue, TSharedContext> = {
    /** In pixels. */
    size: number;

    /**
     * Where this column's value comes from, when it is not simply the accessor's
     * field. Read by `describeTableView` so a consumer can rebuild the column.
     */
    source?: ColumnSource;

    cell?: ColumnDefTemplate<CellContext<DataStackFeatures, TData, TValue, TSharedContext>>;

    /** Extra filter and sort dimensions, each a hidden column filtered and sorted on its own. */
    additionalFilters?: AdditionalFilter<TData, TSharedContext>[];
} & Pick<
    ColumnMeta<DataStackFeatures, TData, TValue, TSharedContext>,
    | 'enableOnRowClick'
    | 'rowLink'
    | 'align'
    | 'description'
    | 'label'
    | 'disableTableCell'
    | 'initialPinning'
    | 'initialVisibility'
    | 'renderType'
    | 'getEllipsisText'
    | 'enableAdvancedFilters'
>;

type CommonColumnOptions<TFilterValue, TCellValue = TFilterValue> = {
    quickFilters?: Array<QuickFilter<TFilterValue>>;
    filterValueTransform?: (value: TFilterValue) => unknown;
    emptyCell?: EmptyCell<TCellValue>;
};

type RequiredDisplayColumnDef<TData extends RowData, TValue, TSharedContext> = Omit<
    DisplayColumnDef<DataStackFeatures, TData, TValue>,
    'cell' | 'filterFn' | 'enablePinning'
> &
    RequiredColumnDef<TData, TValue, TSharedContext> & { id: string };
type RequiredIdentifiedColumnDef<TData extends RowData, TValue, TSharedContext> = Omit<
    IdentifiedColumnDef<DataStackFeatures, TData, TValue>,
    'cell' | 'filterFn' | 'enablePinning'
> &
    RequiredColumnDef<TData, TValue, TSharedContext>;

type TextDisplayColumnDef<TData extends RowData, TValue, TSharedContext> = RequiredDisplayColumnDef<
    TData,
    TValue,
    TSharedContext
> &
    CommonColumnOptions<string, TValue>;

type TextIdentifiedColumnDef<TData extends RowData, TValue, TSharedContext> = RequiredIdentifiedColumnDef<
    TData,
    TValue,
    TSharedContext
> &
    CommonColumnOptions<string, TValue>;

type NumberDisplayColumnDef<TData extends RowData, TValue, TSharedContext> = RequiredDisplayColumnDef<
    TData,
    TValue,
    TSharedContext
> &
    CommonColumnOptions<NumberRange, TValue>;

type NumberIdentifiedColumnDef<TData extends RowData, TValue, TSharedContext> = RequiredIdentifiedColumnDef<
    TData,
    TValue,
    TSharedContext
> &
    CommonColumnOptions<NumberRange, TValue>;

type MonetaryValueDisplayColumnDef<TData extends RowData, TValue, TSharedContext> = RequiredDisplayColumnDef<
    TData,
    TValue,
    TSharedContext
> &
    Omit<MonetaryValueFilterConfig, 'dataType' | 'defaultFilterFn'> &
    CommonColumnOptions<NumberRange, TValue> & {
        /** Lets users change the decimal precision from the column header menu. */
        renderOption?: RenderOptionConfig<MonetaryValueRenderOption>;
    };

type MonetaryValueIdentifiedColumnDef<TData extends RowData, TValue, TSharedContext> = RequiredIdentifiedColumnDef<
    TData,
    TValue,
    TSharedContext
> &
    Omit<MonetaryValueFilterConfig, 'dataType' | 'defaultFilterFn'> &
    CommonColumnOptions<NumberRange, TValue> & {
        /** Lets users change the decimal precision from the column header menu. */
        renderOption?: RenderOptionConfig<MonetaryValueRenderOption>;
    };

type EnumDisplayColumnDef<TData extends RowData, TValue, TSharedContext> = RequiredDisplayColumnDef<
    TData,
    TValue,
    TSharedContext
> &
    Omit<EnumFilterConfig<TData, TValue, TSharedContext>, 'dataType' | 'defaultFilterFn'> &
    CommonColumnOptions<Array<TValue>, TValue>;

type EnumIdentifiedColumnDef<TData extends RowData, TValue, TSharedContext> = RequiredIdentifiedColumnDef<
    TData,
    TValue,
    TSharedContext
> &
    Omit<EnumFilterConfig<TData, TValue, TSharedContext>, 'dataType' | 'defaultFilterFn'> &
    CommonColumnOptions<Array<TValue>, TValue>;

type ArrayDisplayColumnDef<TData extends RowData, TValue, TSharedContext> = RequiredDisplayColumnDef<
    TData,
    TValue,
    TSharedContext
> &
    Omit<ArraryFilterConfig<TData, TValue, TSharedContext>, 'dataType' | 'defaultFilterFn'> &
    CommonColumnOptions<TValue, TValue>;

type ArrayIdentifiedColumnDef<TData extends RowData, TValue, TSharedContext> = RequiredIdentifiedColumnDef<
    TData,
    TValue,
    TSharedContext
> &
    Omit<ArraryFilterConfig<TData, TValue, TSharedContext>, 'dataType' | 'defaultFilterFn'> &
    CommonColumnOptions<TValue, TValue>;

type DateDisplayColumnDef<TData extends RowData, TValue, TSharedContext> = RequiredDisplayColumnDef<
    TData,
    TValue,
    TSharedContext
> &
    CommonColumnOptions<DateRange, TValue>;

type DateIdentifiedColumnDef<TData extends RowData, TValue, TSharedContext> = RequiredIdentifiedColumnDef<
    TData,
    TValue,
    TSharedContext
> &
    CommonColumnOptions<DateRange, TValue>;

/** Narrows `renderOption`, which `CellContext` types as `FormattingType`: widening it would break ~100 cells. */
type QuantityCellContext<TData extends RowData, TValue, TSharedContext> = Omit<
    CellContext<DataStackFeatures, TData, TValue, TSharedContext>,
    'renderOption'
> & {
    renderOption: QuantityRenderOption;
};

type QuantityDisplayColumnDef<TData extends RowData, TValue, TSharedContext> = Omit<
    RequiredDisplayColumnDef<TData, TValue, TSharedContext>,
    'cell'
> & {
    cell?: ColumnDefTemplate<QuantityCellContext<TData, TValue, TSharedContext>>;
} & Omit<QuantityFilterConfig, 'dataType' | 'defaultFilterFn'> &
    CommonColumnOptions<NumberRange, TValue> & {
        renderOption?: RenderOptionConfig<QuantityRenderOption>;
    };

type QuantityIdentifiedColumnDef<TData extends RowData, TValue, TSharedContext> = Omit<
    RequiredIdentifiedColumnDef<TData, TValue, TSharedContext>,
    'cell'
> & {
    cell?: ColumnDefTemplate<QuantityCellContext<TData, TValue, TSharedContext>>;
} & Omit<QuantityFilterConfig, 'dataType' | 'defaultFilterFn'> &
    CommonColumnOptions<NumberRange, TValue> & {
        renderOption?: RenderOptionConfig<QuantityRenderOption>;
    };

type AvailabilityCellContext<TData extends RowData, TValue, TSharedContext> = Omit<
    CellContext<DataStackFeatures, TData, TValue, TSharedContext>,
    'renderOption'
> & {
    renderOption: AvailabilityRenderOption;
};

type AvailabilityDisplayColumnDef<TData extends RowData, TValue, TSharedContext> = Omit<
    RequiredDisplayColumnDef<TData, TValue, TSharedContext>,
    'cell'
> & {
    cell?: ColumnDefTemplate<AvailabilityCellContext<TData, number | null | undefined, TSharedContext>>;
} & CommonColumnOptions<NumberRange, TValue> & {
        renderOption?: RenderOptionConfig<AvailabilityRenderOption>;
    };

type AvailabilityIdentifiedColumnDef<TData extends RowData, TValue, TSharedContext> = Omit<
    RequiredIdentifiedColumnDef<TData, TValue, TSharedContext>,
    'cell'
> & {
    cell?: ColumnDefTemplate<AvailabilityCellContext<TData, number | null | undefined, TSharedContext>>;
} & CommonColumnOptions<NumberRange, TValue> & {
        renderOption?: RenderOptionConfig<AvailabilityRenderOption>;
    };

type ActionColumnDef<TData extends RowData, TSharedContext> = Omit<
    DisplayColumnDef<DataStackFeatures, TData>,
    'cell'
> & {
    /** In pixels. */
    size: number;

    cell?: ColumnDefTemplate<CellContext<DataStackFeatures, TData, unknown, TSharedContext>>;
} & Pick<ColumnMeta<DataStackFeatures, TData, unknown>, 'enableOnRowClick' | 'align' | 'disableTableCell'>;

/** Each column needs an `id` when its accessor is a function. */
type ColumnHelper<TData extends RowData, TSharedContext = unknown> = {
    text: <
        TAccessor extends AccessorFn<TData> | DeepKeys<TData>,
        TValue extends (TAccessor extends AccessorFn<TData, infer TReturn>
            ? TReturn extends string | null | undefined
                ? TReturn
                : unknown
            : TAccessor extends DeepKeys<TData>
              ? DeepValue<TData, TAccessor> extends string | null | undefined
                  ? DeepValue<TData, TAccessor>
                  : unknown
              : never),
    >(
        accessor: TAccessor,
        column: TAccessor extends AccessorFn<TData>
            ? TextDisplayColumnDef<TData, TValue, TSharedContext>
            : TextIdentifiedColumnDef<TData, TValue, TSharedContext>,
    ) => ColumnDef<TData, TValue>;

    number: <
        TAccessor extends AccessorFn<TData> | DeepKeys<TData>,
        TValue extends (TAccessor extends AccessorFn<TData, infer TReturn>
            ? TReturn extends number | null | undefined
                ? TReturn
                : unknown
            : TAccessor extends DeepKeys<TData>
              ? DeepValue<TData, TAccessor> extends number | null | undefined
                  ? DeepValue<TData, TAccessor>
                  : unknown
              : never),
    >(
        accessor: TAccessor,
        column: TAccessor extends AccessorFn<TData>
            ? NumberDisplayColumnDef<TData, TValue, TSharedContext>
            : NumberIdentifiedColumnDef<TData, TValue, TSharedContext>,
    ) => ColumnDef<TData, TValue>;

    enum: <
        TAccessor extends AccessorFn<TData> | DeepKeys<TData>,
        TValue extends (TAccessor extends AccessorFn<TData, infer TReturn>
            ? TReturn
            : TAccessor extends DeepKeys<TData>
              ? DeepValue<TData, TAccessor>
              : never),
    >(
        accessor: TAccessor,
        column: TAccessor extends AccessorFn<TData>
            ? EnumDisplayColumnDef<TData, TValue, TSharedContext>
            : EnumIdentifiedColumnDef<TData, TValue, TSharedContext>,
    ) => ColumnDef<TData, TValue>;

    array: <
        TAccessor extends AccessorFn<TData> | DeepKeys<TData>,
        TValue extends (TAccessor extends AccessorFn<TData, infer TReturn>
            ? TReturn
            : TAccessor extends DeepKeys<TData>
              ? DeepValue<TData, TAccessor> extends Array<unknown> | null | undefined
                  ? DeepValue<TData, TAccessor>
                  : never
              : never),
    >(
        accessor: TAccessor,
        column: TAccessor extends AccessorFn<TData>
            ? ArrayDisplayColumnDef<TData, TValue, TSharedContext>
            : ArrayIdentifiedColumnDef<TData, TValue, TSharedContext>,
    ) => ColumnDef<TData, TValue>;

    monetaryValue: <
        TAccessor extends AccessorFn<TData> | DeepKeys<TData>,
        TValue extends (TAccessor extends AccessorFn<TData, infer TReturn>
            ? TReturn extends MonetaryValue | null | undefined
                ? TReturn
                : unknown
            : TAccessor extends DeepKeys<TData>
              ? DeepValue<TData, TAccessor> extends MonetaryValue | null | undefined
                  ? DeepValue<TData, TAccessor>
                  : unknown
              : never),
    >(
        accessor: TAccessor,
        column: TAccessor extends AccessorFn<TData>
            ? MonetaryValueDisplayColumnDef<TData, TValue, TSharedContext>
            : MonetaryValueIdentifiedColumnDef<TData, TValue, TSharedContext>,
    ) => ColumnDef<TData, TValue>;

    date: <
        TAccessor extends AccessorFn<TData> | DeepKeys<TData>,
        TValue extends (TAccessor extends AccessorFn<TData, infer TReturn>
            ? TReturn extends string | null | undefined
                ? TReturn
                : unknown
            : TAccessor extends DeepKeys<TData>
              ? DeepValue<TData, TAccessor> extends string | null | undefined
                  ? DeepValue<TData, TAccessor>
                  : unknown
              : never),
    >(
        accessor: TAccessor,
        column: TAccessor extends AccessorFn<TData>
            ? DateDisplayColumnDef<TData, TValue, TSharedContext>
            : DateIdentifiedColumnDef<TData, TValue, TSharedContext>,
    ) => ColumnDef<TData, TValue>;

    /**
     * Creates a column definition for quantity data (`Quantity` from `@luminovo/http-client`).
     * Filters and sorts on the base-unit value, and adds type and unit sub-filters.
     */
    quantity: <
        TAccessor extends AccessorFn<TData> | DeepKeys<TData>,
        TValue extends (TAccessor extends AccessorFn<TData, infer TReturn>
            ? TReturn extends Quantity | null | undefined
                ? TReturn
                : unknown
            : TAccessor extends DeepKeys<TData>
              ? DeepValue<TData, TAccessor> extends Quantity | null | undefined
                  ? DeepValue<TData, TAccessor>
                  : unknown
              : never),
    >(
        accessor: TAccessor,
        column: TAccessor extends AccessorFn<TData>
            ? QuantityDisplayColumnDef<TData, TValue, TSharedContext>
            : QuantityIdentifiedColumnDef<TData, TValue, TSharedContext>,
    ) => ColumnDef<TData, TValue>;

    /** A `SourcingAvailability` column: sorts and filters on `delivery_time_in_days`, and adds a "Status" filter. */
    availability: <
        TAccessor extends AccessorFn<TData> | DeepKeys<TData>,
        TValue extends (TAccessor extends AccessorFn<TData, infer TReturn>
            ? TReturn extends SourcingAvailability | null | undefined
                ? TReturn
                : unknown
            : TAccessor extends DeepKeys<TData>
              ? DeepValue<TData, TAccessor> extends SourcingAvailability | null | undefined
                  ? DeepValue<TData, TAccessor>
                  : unknown
              : never),
    >(
        accessor: TAccessor,
        column: TAccessor extends AccessorFn<TData>
            ? AvailabilityDisplayColumnDef<TData, TValue, TSharedContext>
            : AvailabilityIdentifiedColumnDef<TData, TValue, TSharedContext>,
    ) => ColumnDef<TData, TValue>;

    /** @deprecated Use `text`, `number`, `array` or `enum` where possible. */
    generic: <
        TAccessor extends AccessorFn<TData> | DeepKeys<TData>,
        TValue extends (TAccessor extends AccessorFn<TData, infer TReturn>
            ? TReturn
            : TAccessor extends DeepKeys<TData>
              ? DeepValue<TData, TAccessor>
              : never),
    >(
        accessor: TAccessor,
        column: TAccessor extends AccessorFn<TData>
            ? RequiredDisplayColumnDef<TData, TValue, TSharedContext>
            : RequiredIdentifiedColumnDef<TData, TValue, TSharedContext>,
    ) => ColumnDef<TData, TValue>;

    /** KNOWN ISSUE: `cell` is mistyped together with a `sharedContext`; unfixed, as nothing needs that yet. */
    action: (column: ActionColumnDef<TData, TSharedContext>) => ColumnDef<TData, unknown>;

    /**
     * Creates a column group that visually and logically groups multiple leaf columns under a shared header.
     * Group columns have no accessor and are not sortable/filterable.
     * Toggling a group's visibility in the column manager toggles all its children.
     */
    group: (column: {
        id: string;
        header: string | (() => string);
        columns: ColumnDef<TData, any>[];
    }) => ColumnDef<TData, unknown>;
};

export function createColumnHelper<TData extends RowData, TSharedContext = unknown>(): ColumnHelper<
    TData,
    TSharedContext
> {
    type TanStackParemeter<TData extends RowData> = Parameters<
        TanStackColumnHelper<DataStackFeatures, TData>['accessor']
    >;
    type TanStackAccessor<TData extends RowData> = TanStackParemeter<TData>[0];
    type TanStackColumn<TData extends RowData> = TanStackParemeter<TData>[1];

    type InnerAccessor<TData extends RowData> = TanStackAccessor<TData>;
    type InnerColumn<TData extends RowData> = Omit<TanStackColumn<TData>, 'filterFn' | 'size'> &
        RequiredColumnDef<TData, unknown, unknown> & { id: string } & CommonColumnOptions<unknown, unknown>;

    const columnHelper = tanStackCreateColumnHelper<DataStackFeatures, TData>();

    function genericAccessor(
        accessor: InnerAccessor<TData>,
        {
            enableOnRowClick,
            rowLink,
            align,
            description,
            label,
            quickFilters,
            enableAdvancedFilters,
            filterValueTransform,
            disableTableCell,
            initialPinning,
            initialVisibility,
            renderType,
            getEllipsisText,
            emptyCell,
            additionalFilters,
            source,
            meta,
            ...column
        }: InnerColumn<TData> & { meta: Partial<ColumnMeta<DataStackFeatures, TData, any>> },
    ) {
        return columnHelper.accessor(accessor, {
            header: label,
            filterFn: 'buildFilter',
            sortUndefined: 'last',
            ...column,
            id: typeof accessor === 'string' && !column.id ? accessor : column.id,
            meta: {
                ...meta,
                dataType: meta?.dataType ?? 'generic',
                enableOnRowClick,
                rowLink,
                align: align ?? meta.align,
                description,
                ...(source === undefined ? {} : { source }),
                label,
                quickFilters,
                filterValueTransform,
                enableAdvancedFilters: enableAdvancedFilters ?? true,
                disableTableCell,
                initialPinning,
                initialVisibility,
                renderType,
                getEllipsisText,
                emptyCell,
                additionalFilters,
            },
        });
    }

    function textAccessor(
        accessorFn: InnerAccessor<TData>,
        column: InnerColumn<TData> & Omit<TextFilterConfig, 'filterFn'>,
    ) {
        return genericAccessor(accessorFn, {
            ...column,
            meta: {
                filterConfig: {
                    dataType: 'text',
                    defaultFilterFn: SupportedFilterFn.includesString,
                },
                dataType: 'text',
            },
        });
    }

    function numberAccessor(
        accessorFn: InnerAccessor<TData>,
        column: InnerColumn<TData> & Omit<NumberFilterConfig, 'filterFn'>,
    ) {
        return genericAccessor(accessorFn, {
            ...column,
            meta: {
                filterConfig: {
                    dataType: 'number',
                    defaultFilterFn: SupportedFilterFn.inNumberRange,
                },
                dataType: 'number',
                align: 'right',
            },
        });
    }

    function monetaryValueAccessor(
        accessorFn: InnerAccessor<TData>,
        column: InnerColumn<TData> &
            Omit<MonetaryValueFilterConfig, 'filterFn'> & {
                renderOption?: RenderOptionConfig<MonetaryValueRenderOption>;
            },
    ) {
        const { renderOption, ...restColumn } = column;
        return genericAccessor(accessorFn, {
            enableGlobalFilter: false,
            ...(restColumn as InnerColumn<TData>),
            sortFn: 'monetaryValue',
            meta: {
                filterConfig: {
                    dataType: 'monetaryValue',
                    defaultFilterFn: SupportedFilterFn.inMonetaryValueRange,
                },
                dataType: 'monetaryValue',
                align: 'right',
                renderOptionConfig: renderOption,
            },
        });
    }

    function dateAccessor(
        accessorFn: InnerAccessor<TData>,
        column: InnerColumn<TData> & Omit<DateFilterConfig, 'filterFn'>,
    ) {
        return genericAccessor(accessorFn, {
            enableGlobalFilter: false,
            ...column,
            sortFn: 'date',
            meta: {
                filterConfig: {
                    dataType: 'date',
                    defaultFilterFn: SupportedFilterFn.inDateRange,
                },
                dataType: 'date',
            },
        });
    }

    function enumAccessor(
        accessorFn: InnerAccessor<TData>,
        column: InnerColumn<TData> &
            Omit<EnumFilterConfig<TData>, 'filterFn'> & {
                additionalSearchValue?: (row: Row<DataStackFeatures, TData>) => string | undefined;
            },
    ) {
        const getOptionLabel = (value: TData) => (isPresent(value) ? column.getOptionLabel(value) : t`Unknown`);

        return genericAccessor(accessorFn, {
            ...column,
            sortFn: createPositionBasedSortFunction<TData>({ options: column.options, getOptionLabel }),
            meta: {
                dataType: 'enum',
                filterConfig: {
                    dataType: 'enum',
                    defaultFilterFn: SupportedFilterFn.equalsAny,
                    options: column.options,
                    getOptionLabel: getOptionLabel,
                    renderOption: column.renderOption,
                    getOptionCount: column.getOptionCount,
                    getOptionKey: column.getOptionKey,
                    additionalSearchValues: column.additionalSearchValues,
                },
            },
        });
    }

    function arrayAccessor(
        accessorFn: InnerAccessor<TData>,
        column: InnerColumn<TData> & Omit<ArraryFilterConfig<TData>, 'filterFn'>,
    ) {
        return genericAccessor(accessorFn, {
            ...column,
            meta: {
                dataType: 'array',
                filterConfig: {
                    dataType: 'array',
                    defaultFilterFn: SupportedFilterFn.arrIncludesSome,
                    options: column.options,
                    getOptionLabel: (value: TData) => (isPresent(value) ? column.getOptionLabel(value) : t`Unknown`),
                    renderOption: column.renderOption,
                    getOptionCount: column.getOptionCount,
                    getOptionKey: column.getOptionKey,
                },
            },
        });
    }

    function quantityAccessor(
        accessorFn: InnerAccessor<TData>,
        column: InnerColumn<TData> &
            Omit<QuantityFilterConfig, 'dataType' | 'defaultFilterFn'> & {
                renderOption?: RenderOptionConfig<QuantityRenderOption>;
            },
    ) {
        const { renderOption, additionalFilters: userAdditionalFilters, cell: userCell, ...restColumn } = column;

        const defaultQuantityCell: ColumnDefTemplate<
            QuantityCellContext<TData, Quantity | null | undefined, TSharedContext>
        > = (info) => {
            const v = info.getValue();
            if (!isPresent(v)) {
                return '-';
            }
            return formatQuantity(v, { renderOption: info.renderOption });
        };

        const columnId = typeof accessorFn === 'string' ? accessorFn : restColumn.id;
        const getQty = (row: TData, index: number) =>
            (typeof accessorFn === 'string' ? (row as any)[accessorFn] : accessorFn(row, index)) as
                | Quantity
                | null
                | undefined;

        const quantityTypeLabels: Record<QuantityType, () => string> = {
            CardinalNumber: () => t`Cardinal number`,
            Weight: () => t`Weight`,
            Length: () => t`Length`,
            Volume: () => t`Volume`,
        };

        const typeFilter: AdditionalFilter<TData> = {
            id: `${columnId}_type`,
            label: () => t`Type`,
            dataType: 'enum',
            accessor: (row: TData, index: number) => getQty(row, index)?.type ?? null,
            getOptionLabel: (option: QuantityType) => quantityTypeLabels[option]?.() ?? String(option),
        };

        const unitFilter: AdditionalFilter<TData> = {
            id: `${columnId}_unit`,
            label: () => t`Unit`,
            dataType: 'enum',
            accessor: (row: TData, index: number) => getQty(row, index)?.unit ?? null,
            getOptionLabel: (option: QuantityUnit) => formatQuantityUnit(option),
        };

        return genericAccessor(accessorFn, {
            enableGlobalFilter: false,
            ...(restColumn as InnerColumn<TData>),
            cell: userCell ?? defaultQuantityCell,
            additionalFilters: [typeFilter, unitFilter, ...(userAdditionalFilters ?? [])] as AdditionalFilter<TData>[],
            sortFn: 'quantity',
            meta: {
                filterConfig: {
                    dataType: 'quantity',
                    defaultFilterFn: SupportedFilterFn.inQuantityRange,
                },
                dataType: 'quantity',
                align: 'right',
                renderOptionConfig: renderOption ?? { default: 'base-unit' },
            },
        });
    }

    function availabilityAccessor(
        accessorFn: InnerAccessor<TData>,
        column: InnerColumn<TData> &
            Omit<NumberFilterConfig, 'dataType' | 'defaultFilterFn'> & {
                renderOption?: RenderOptionConfig<AvailabilityRenderOption>;
            },
    ) {
        const { renderOption, additionalFilters: userAdditionalFilters, cell: userCell, ...restColumn } = column;

        const columnId = typeof accessorFn === 'string' ? accessorFn : restColumn.id;

        const getAvailability = (row: TData, index: number) =>
            (typeof accessorFn === 'string' ? (row as any)[accessorFn] : accessorFn(row, index)) as
                | SourcingAvailability
                | null
                | undefined;

        const defaultAvailabilityCell: ColumnDefTemplate<
            AvailabilityCellContext<TData, number | null | undefined, TSharedContext>
        > = (info) => {
            return formatAvailabilityDuration(info.getValue(), info.renderOption);
        };

        const availabilityStatusLabels: Record<SourcingAvailabilityStatus, () => string> = {
            [SourcingAvailabilityStatus.InStock]: () => t`In stock`,
            [SourcingAvailabilityStatus.LeadTime]: () => t`Lead time`,
            [SourcingAvailabilityStatus.OnOrder]: () => t`On order`,
            [SourcingAvailabilityStatus.OnOrderUnknownLeadTime]: () => t`On order (unknown)`,
            [SourcingAvailabilityStatus.NotSpecified]: () => t`Not specified`,
        };

        const statusFilter: AdditionalFilter<TData> = {
            id: `${columnId}_status`,
            label: () => t`Status`,
            dataType: 'enum',
            accessor: (row: TData, index: number) => getAvailability(row, index)?.status ?? null,
            getOptionLabel: (option: SourcingAvailabilityStatus) =>
                availabilityStatusLabels[option]?.() ?? String(option),
        };

        return genericAccessor(
            (row: TData, index: number) => getAvailability(row, index)?.delivery_time_in_days ?? null,
            {
                enableGlobalFilter: false,
                ...(restColumn as InnerColumn<TData>),
                cell: userCell ?? defaultAvailabilityCell,
                additionalFilters: [statusFilter, ...(userAdditionalFilters ?? [])] as AdditionalFilter<TData>[],
                meta: {
                    filterConfig: {
                        dataType: 'number',
                        defaultFilterFn: SupportedFilterFn.inNumberRange,
                    },
                    dataType: 'availability',
                    align: 'right',
                    renderOptionConfig: renderOption ?? { default: 'days' },
                },
            },
        );
    }

    function action({
        enableOnRowClick = false,
        align,
        disableTableCell,
        meta,
        ...column
    }: ActionColumnDef<TData, TSharedContext>) {
        return columnHelper.display({
            ...column,
            enableHiding: false,
            meta: {
                dataType: 'generic',
                label: () => t`Unknown`,
                enableOnRowClick,
                align,
                disableTableCell,
                ...meta,
            },
        } as any);
    }
    function group({
        id,
        header,
        columns,
    }: {
        id: string;
        header: string | (() => string);
        columns: ColumnDef<TData, any>[];
    }) {
        const headerFn = typeof header === 'function' ? header : () => header;
        const totalSize = columns.reduce((sum, col) => sum + ((col as any).size ?? 150), 0);
        return {
            id,
            header: headerFn,
            size: totalSize,
            columns,
            enableSorting: false,
            enableColumnFilter: false,
            enableGlobalFilter: false,
            enableHiding: false,
            meta: {
                label: headerFn,
                dataType: 'generic' as const,
            },
        } as GroupColumnDef<DataStackFeatures, TData, unknown>;
    }

    // Safe: `ColumnHelper<TData>` types the outside, and `Accessor<TData>` and `Column<TData>` the inside.
    return {
        text: textAccessor as any,
        number: numberAccessor as any,
        enum: enumAccessor as any,
        array: arrayAccessor as any,
        monetaryValue: monetaryValueAccessor as any,
        date: dateAccessor as any,
        quantity: quantityAccessor as any,
        availability: availabilityAccessor as any,
        generic: genericAccessor as any,
        action: action as any,
        group: group as any,
    };
}
